import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, CalendarPlus, Instagram } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { NYE_EVENT } from "@/data/nye";

// ---------- .ics (iCalendar) helper ---------------------------------------
// Builds a save-the-date file client-side so there's no network round-trip
// and no backend endpoint to maintain. All event times are hardcoded in UTC
// because the Jan 1, 2027 7 PM – 1 AM window at Rose & Rye (Atlanta) sits in
// US Eastern Standard Time (UTC-5), with no DST crossing to worry about.
const ICS_START_UTC = "20270102T000000Z"; // 2027-01-01 19:00 ET
const ICS_END_UTC = "20270102T060000Z";   // 2027-01-02 01:00 ET

function escapeIcsText(value) {
    // Per RFC 5545: commas, semicolons, backslashes, and newlines must be escaped.
    return String(value).replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
}

function buildNyeIcs() {
    const dtstamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const summary = escapeIcsText(`${NYE_EVENT.title} — ${NYE_EVENT.subtitle}`);
    const location = escapeIcsText(`${NYE_EVENT.venue} · ${NYE_EVENT.city}`);
    const description = escapeIcsText(
        `${NYE_EVENT.tagline}\n\nPresented by ${NYE_EVENT.presenter}.\n` +
        `${NYE_EVENT.time_label}. ${NYE_EVENT.price_advance}; ${NYE_EVENT.price_door}.\n` +
        `Dress code: ${NYE_EVENT.dress_code}\n\nTickets: ${NYE_EVENT.ticket_url}`
    );
    return [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "PRODID:-//Mrs.Black(In)Love//A Jazz Affair//EN",
        "CALSCALE:GREGORIAN",
        "METHOD:PUBLISH",
        "BEGIN:VEVENT",
        `UID:${NYE_EVENT.slug}@mrsblackinlove.org`,
        `DTSTAMP:${dtstamp}`,
        `DTSTART:${ICS_START_UTC}`,
        `DTEND:${ICS_END_UTC}`,
        `SUMMARY:${summary}`,
        `DESCRIPTION:${description}`,
        `LOCATION:${location}`,
        `URL:${NYE_EVENT.ticket_url}`,
        "END:VEVENT",
        "END:VCALENDAR",
    ].join("\r\n");
}

function downloadNyeSaveTheDate() {
    const blob = new Blob([buildNyeIcs()], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "a-jazz-affair-new-years-2027.ics";
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Give the browser a tick to start the download before we revoke.
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    toast.success("Save the date — added to your calendar app");
}

export default function Experiences() {
    const [events, setEvents] = useState([]);
    const location = useLocation();
    useEffect(() => {
        api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    }, []);

    // Anchor-scroll to the NYE card when arriving from `/experiences#new-years-jazz-affair`.
    useEffect(() => {
        if (!location.hash) return;
        const el = document.getElementById(location.hash.replace("#", ""));
        if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }, [location.hash]);

    const upcoming = useMemo(() => {
        const now = new Date();
        return events.filter((e) => new Date(e.date_iso) >= now).sort((a, b) => new Date(a.date_iso) - new Date(b.date_iso));
    }, [events]);
    const past = useMemo(() => {
        const now = new Date();
        return events.filter((e) => new Date(e.date_iso) < now).sort((a, b) => new Date(b.date_iso) - new Date(a.date_iso));
    }, [events]);

    return (
        <main data-testid="experiences-page" className="pt-32 pb-32 min-h-screen">
            <section className="mx-auto max-w-[1600px] px-6 lg:px-12">
                <p className="eyebrow">Experiences</p>
                <h1 className="font-display text-6xl md:text-8xl mt-6 max-w-4xl">A season, in <em className="text-[color:var(--gold)]">small rooms</em>.</h1>
                <p className="mt-8 max-w-2xl text-[color:var(--cream-muted)] text-lg">
                    Curated gatherings across cities. Each one intentionally small. Each one designed to feel like the best evening you've had all year.
                </p>
            </section>

            {/* FEATURED — A Jazz Affair, New Year's Day 2027. Hardcoded hero card
                because the event is managed outside the backend events store. */}
            <section
                id={NYE_EVENT.slug}
                className="mx-auto max-w-[1600px] px-6 lg:px-12 mt-24 scroll-mt-32"
                data-testid="nye-featured"
            >
                <div className="flex items-baseline gap-6">
                    <p className="eyebrow">Featured · New Year's Day</p>
                    <div className="hairline" />
                </div>
                <article className="mt-10 grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-7 relative overflow-hidden" data-testid="nye-hero-media">
                        <div className="aspect-[1/1] md:aspect-[4/5] relative overflow-hidden border border-[color:var(--gold)]/30 bg-black">
                            {NYE_EVENT.video_url ? (
                                <video
                                    src={NYE_EVENT.video_url}
                                    poster={NYE_EVENT.hero_image}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    className="absolute inset-0 h-full w-full object-cover"
                                />
                            ) : (
                                <img src={NYE_EVENT.hero_image} alt="A Jazz Affair — Queer New Year's Celebration" className="absolute inset-0 h-full w-full object-contain" />
                            )}
                        </div>
                        {NYE_EVENT.instagram_reel_url && (
                            <a
                                href={NYE_EVENT.instagram_reel_url}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-4 inline-flex items-center justify-center gap-2 text-xs tracking-[0.24em] uppercase text-[color:var(--gold)] border border-[color:var(--gold)]/50 hover:border-[color:var(--gold)] hover:bg-[color:var(--gold)]/5 transition-colors duration-300 px-5 py-3 w-full"
                                data-testid="nye-instagram-reel-experiences"
                            >
                                <Instagram size={14} strokeWidth={1.5} /> Watch the full version on Instagram
                            </a>
                        )}
                    </div>
                    <div className="lg:col-span-5">
                        <p className="eyebrow">{NYE_EVENT.venue} · {NYE_EVENT.city}</p>
                        <h2 className="font-display text-5xl md:text-7xl mt-4 leading-[0.95]">
                            A <em className="text-[color:var(--gold)]">Jazz</em> Affair.
                        </h2>
                        <p className="mt-3 font-serif italic text-xl text-[color:var(--cream-muted)]">{NYE_EVENT.subtitle}</p>
                        <p className="mt-6 text-[color:var(--soft-cream)] text-lg font-serif leading-relaxed">{NYE_EVENT.tagline}</p>
                        <div className="mt-6 flex flex-wrap gap-2 text-xs" data-testid="nye-highlights">
                            {NYE_EVENT.highlights.map((h) => (
                                <span key={h} className="border border-[color:var(--espresso)] px-3 py-1 text-[color:var(--cream-muted)]">{h}</span>
                            ))}
                        </div>
                        <dl className="mt-8 grid grid-cols-2 gap-y-4 gap-x-6 text-sm">
                            <div>
                                <dt className="text-[0.65rem] uppercase tracking-[0.28em] text-[color:var(--cream-muted)]">Time</dt>
                                <dd className="mt-1 text-[color:var(--soft-cream)]">{NYE_EVENT.time_label}</dd>
                            </div>
                            <div>
                                <dt className="text-[0.65rem] uppercase tracking-[0.28em] text-[color:var(--cream-muted)]">Admission</dt>
                                <dd className="mt-1 text-[color:var(--soft-cream)]">{NYE_EVENT.price_advance}</dd>
                            </div>
                            <div>
                                <dt className="text-[0.65rem] uppercase tracking-[0.28em] text-[color:var(--cream-muted)]">Door</dt>
                                <dd className="mt-1 text-[color:var(--soft-cream)]">{NYE_EVENT.price_door}</dd>
                            </div>
                            <div>
                                <dt className="text-[0.65rem] uppercase tracking-[0.28em] text-[color:var(--cream-muted)]">Dress code</dt>
                                <dd className="mt-1 text-[color:var(--soft-cream)]">Roaring '20s Glam</dd>
                            </div>
                        </dl>
                        <div className="mt-10 flex flex-wrap gap-4 items-center">
                            <a
                                href={NYE_EVENT.ticket_url}
                                target="_blank"
                                rel="noreferrer"
                                className="btn-gold btn-gold-solid"
                                data-testid="nye-purchase-featured"
                            >
                                Purchase your ticket <ArrowRight size={14} strokeWidth={1.5} />
                            </a>
                            <button
                                type="button"
                                onClick={downloadNyeSaveTheDate}
                                className="btn-gold"
                                data-testid="nye-save-the-date"
                            >
                                <CalendarPlus size={14} strokeWidth={1.5} /> Save the date
                            </button>
                            <a
                                href={`sms:${NYE_EVENT.section_phone.replace(/\D/g, "")}`}
                                className="btn-gold"
                                data-testid="nye-section-inquire"
                            >
                                Reserve a section
                            </a>
                        </div>
                    </div>
                </article>

                <div className="mt-14 grid lg:grid-cols-12 gap-10" data-testid="nye-description">
                    <div className="lg:col-span-7 space-y-5 text-[color:var(--cream-muted)] text-lg leading-relaxed font-serif">
                        {NYE_EVENT.long_description.map((p) => (
                            <p key={p.slice(0, 24)}>{p}</p>
                        ))}
                    </div>
                    <aside className="lg:col-span-5 border border-[color:var(--espresso)] p-8 bg-[color:var(--dark-grey)]/40">
                        <p className="eyebrow">The details</p>
                        <ul className="mt-6 space-y-4 text-sm text-[color:var(--soft-cream)] font-serif">
                            <li className="flex items-baseline gap-3"><span className="text-[color:var(--gold)]">—</span><span>Date: {NYE_EVENT.date_label}</span></li>
                            <li className="flex items-baseline gap-3"><span className="text-[color:var(--gold)]">—</span><span>Time: {NYE_EVENT.time_label}</span></li>
                            <li className="flex items-baseline gap-3"><span className="text-[color:var(--gold)]">—</span><span>Location: {NYE_EVENT.venue}, {NYE_EVENT.city}</span></li>
                            <li className="flex items-baseline gap-3"><span className="text-[color:var(--gold)]">—</span><span>Presented by: {NYE_EVENT.presenter}</span></li>
                            <li className="flex items-baseline gap-3"><span className="text-[color:var(--gold)]">—</span><span>Admission: {NYE_EVENT.price_advance} · {NYE_EVENT.price_door}</span></li>
                        </ul>
                        <p className="mt-6 text-xs text-[color:var(--cream-muted)] leading-relaxed border-t border-[color:var(--espresso)] pt-5">
                            Online ticket sales are subject to venue capacity. If max occupancy is reached through advance sales, no tickets will be available at the door. <strong className="text-[color:var(--soft-cream)]">No ticket, no entry.</strong>
                        </p>
                        <p className="mt-4 text-xs text-[color:var(--cream-muted)] leading-relaxed">
                            This is an intentional queer women-only event. Guests are asked to respect the purpose and safety of the space.
                        </p>
                        <p className="mt-4 text-xs text-[color:var(--cream-muted)] leading-relaxed">
                            To reserve a section, DM Mrs.Black(In)Love or text <a href={`sms:${NYE_EVENT.section_phone.replace(/\D/g, "")}`} className="text-[color:var(--gold)] underline">{NYE_EVENT.section_phone}</a> with your name and email.
                        </p>
                    </aside>
                </div>

                {NYE_EVENT.flyer_images.length > 0 && (
                    <div className="mt-14" data-testid="nye-flyer-strip">
                        <p className="eyebrow mb-6">More from the night</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {NYE_EVENT.flyer_images.map((f, i) => (
                                <figure key={f.src} className="overflow-hidden border border-[color:var(--espresso)] bg-black" data-testid={`nye-flyer-${i}`}>
                                    <img src={f.src} alt={f.label} className="w-full h-full object-contain aspect-square" />
                                </figure>
                            ))}
                        </div>
                    </div>
                )}
            </section>

            <section className="mx-auto max-w-[1600px] px-6 lg:px-12 mt-24">
                <div className="flex items-baseline gap-6">
                    <p className="eyebrow">Upcoming</p>
                    <div className="hairline" />
                </div>
                <div className="mt-14 space-y-24">
                    {upcoming.map((e, i) => (
                        <article key={e.id} className={`grid lg:grid-cols-12 gap-10 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                            <Link to={`/experiences/${e.slug}`} className="lg:col-span-7 zoom-parent block overflow-hidden" data-testid={`experience-image-${i}`}>
                                <img src={e.hero_image} alt={e.title} className="h-[560px] w-full object-cover" />
                            </Link>
                            <div className="lg:col-span-5">
                                <p className="eyebrow">{e.city}{e.date_confirmed === false ? " · Date coming soon" : ` · ${new Date(e.date_iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`}</p>
                                <h2 className="font-display text-5xl md:text-6xl mt-4">{e.title}</h2>
                                <p className="mt-5 text-[color:var(--cream-muted)]">{e.tagline}</p>
                                <div className="mt-6 flex flex-wrap gap-2 text-xs">
                                    {e.highlights.slice(0, 3).map((h) => (
                                        <span key={h} className="border border-[color:var(--espresso)] px-3 py-1 text-[color:var(--cream-muted)]">{h}</span>
                                    ))}
                                </div>
                                <div className="mt-8 flex flex-wrap gap-4 items-center">
                                    {e.is_coming_soon ? (
                                        <>
                                            <span className="btn-gold pointer-events-none" data-testid={`coming-soon-${i}`}>Details coming soon</span>
                                            <a href="/#letters-from-the-table" className="link-underline text-sm">Join the list →</a>
                                        </>
                                    ) : (
                                        <>
                                            <Link to={`/experiences/${e.slug}`} className="btn-gold" data-testid={`experience-cta-${i}`}>
                                                View & reserve
                                            </Link>
                                            {e.tiers.length > 0 && (
                                                <span className="text-sm text-[color:var(--cream-muted)]">
                                                    From ${Math.min(...e.tiers.map((t) => t.price_cents)) / 100}
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            {past.length > 0 && (
                <section className="mx-auto max-w-[1600px] px-6 lg:px-12 mt-40 pt-20 border-t border-[color:var(--espresso)]" data-testid="previously-section">
                    <div className="flex items-baseline gap-6">
                        <p className="eyebrow">Previously</p>
                        <div className="hairline" />
                    </div>
                    <h2 className="font-display text-5xl md:text-7xl mt-6 max-w-3xl">Rooms we've <em className="text-[color:var(--gold)]">already lived in</em>.</h2>
                    <p className="mt-6 max-w-2xl text-[color:var(--cream-muted)]">A record of the evenings that made this what it is.</p>
                    <div className="mt-16 grid md:grid-cols-2 gap-x-8 gap-y-16">
                        {past.map((e, i) => (
                            <article key={e.id} data-testid={`past-experience-${i}`} className="zoom-parent group">
                                <div className="overflow-hidden relative">
                                    <img src={e.hero_image} alt={e.title} className="h-[440px] w-full object-cover opacity-90" />
                                    <span className="absolute top-4 left-4 bg-[color:var(--warm-black)]/80 backdrop-blur-md text-[color:var(--gold)] text-[0.65rem] tracking-[0.24em] uppercase px-3 py-1">Concluded</span>
                                </div>
                                <p className="mt-6 eyebrow">{e.city} · {new Date(e.date_iso).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
                                <h3 className="font-serif text-3xl mt-2 group-hover:text-[color:var(--gold)] transition-colors duration-300">{e.title}</h3>
                                <p className="mt-3 text-sm text-[color:var(--cream-muted)]">{e.tagline}</p>
                            </article>
                        ))}
                    </div>
                </section>
            )}
        </main>
    );
}
